// ViewHistory.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import { API_BASE } from '../../config/api.js';

// Table columns (match history view output)
const columns = [
  { name: 'QueryID', selector: row => row.QueryID, sortable: true },
  { name: 'QueryStatus', selector: row => row.QueryStatus, sortable: true },
  { name: 'CreatedOn', selector: row => row.CreatedOn, sortable: true },
  { name: 'CreatedBy', selector: row => row.CreatedBy, sortable: true },
  { name: 'ModifiedOn', selector: row => row.ModifiedOn, sortable: true },
  { name: 'ModifiedBy', selector: row => row.ModifiedBy, sortable: true },
  { name: 'ClosedOn', selector: row => row.ClosedOn, sortable: true },
  { name: 'ClosedBy', selector: row => row.ClosedBy, sortable: true },
  { name: 'ShortQuery', selector: row => row.ShortQuery, sortable: true },
  { name: 'Query', selector: row => row.Query, sortable: true },
  { name: 'Solution', selector: row => row.Solution, sortable: true },
  { name: 'Category', selector: row => row.Category, sortable: true },
  { name: 'SubCategory', selector: row => row.SubCategory, sortable: true },
  { name: 'SMSTEXT', selector: row => row.SMSTEXT, sortable: true },
  { name: 'ResolutionProvided', selector: row => row.ResolutionProvided, sortable: true },
  { name: 'Office', selector: row => row.Office, sortable: true },
 
];

// Local seed row if the API returns no data for a known QueryID.
const fallbackRowsByQueryId = {
  '286072': [
    {
      QueryID: 286072,
      QueryStatus: 'Initiated',
      CreatedOn: '2026-01-01 10:18:44.937',
      CreatedBy: 'Maha Fatima',
      ModifiedOn: '2026-01-01 10:19:37.167',
      ModifiedBy: 'Maha Fatima',
      ClosedOn: null,
      ClosedBy: null,
      ShortQuery: 0,
      Query: 'qry',
      Solution: 'sol',
      Category: 'Civil Law',
      SubCategory: 'CVL-Banking',
      SMSTEXT: null,
      ResolutionProvided: '',
      Office: 'Call Center',
      TotalTags: 3,
      TotalNotes: 0,
      HistoryCount: 0,
    },
  ],
};

/** ---------- small helpers ---------- */
function asValue(value) {
  return value === null || value === undefined ? '' : value;
}

function normalizeHistoryRow(row = {}) {
  return {
    QueryID: asValue(
      row.QueryID ??
      row.QueryId ??
      row.queryId ??
      row.queryid ??
      row.ComplaintID ??
      row.complaintid ??
      row.QID ??
      row.qid ??
      ''
    ),
    QueryStatus: asValue(
      row.QueryStatus ??
      row.queryStatus ??
      row.querystatus ??
      row.JobStatus ??
      row.jobstatus ??
      row.Status ??
      row.status ??
      ''
    ),
    CreatedOn: asValue(
      row.CreatedOn ??
      row.createdOn ??
      row.createdon ??
      row.LaunchedOn ??
      ''
    ),
    CreatedBy: asValue(
      row.CreatedBy ??
      row.Created ??
      row.createdBy ??
      row.createdby ??
      row.Agent ??
      row.Agentid ??
      ''
    ),
    ModifiedOn: asValue(
      row.ModifiedOn ??
      row.modifiedOn ??
      row.modifiedon ??
      row.EditOn ??
      row.editon ??
      row.AcceptedOn ??
      ''
    ),
    ModifiedBy: asValue(
      row.ModifiedBy ??
      row.Modified ??
      row.modifiedBy ??
      row.modifiedby ??
      row.EditBy ??
      row.editby ??
      ''
    ),
    ClosedOn: asValue(row.ClosedOn ?? row.closedOn ?? row.closedon ?? ''),
    ClosedBy: asValue(row.ClosedBy ?? row.closedBy ?? row.closedby ?? ''),
    ShortQuery: asValue(
      row.ShortQuery ??
      row.shortQuery ??
      row.shortquery ??
      row.AdditionalInfo8 ??
      ''
    ),
    Query: asValue(row.Query ?? row.query ?? row.additionalinfo9 ?? ''),
    Solution: asValue(row.Solution ?? row.solution ?? row.additionalinfo10 ?? ''),
    Category: asValue(
      row.Category ??
      row.MainCategory ??
      row.mainCategory ??
      row.category ??
      row.CategoryName ??
      row.categoryname ??
      ''
    ),
    SubCategory: asValue(
      row.SubCategory ??
      row.subCategory ??
      row.subcategory ??
      row.SubCategoryName ??
      row.subcategoryname ??
      ''
    ),
    SMSTEXT: asValue(
      row.SMSTEXT ??
      row.SMSText ??
      row.SMS ??
      row.smsText ??
      row.smstext ??
      row.additionalinfo6 ??
      ''
    ),
    ResolutionProvided: asValue(
      row.ResolutionProvided ??
      row.resolutionProvided ??
      row.resolutionprovided ??
      ''
    ),
    Office: asValue(
      row.Office ??
      row.RoutedToDepartment ??
      row.routedtodepartment ??
      row.office ??
      ''
    ),
    TotalTags: asValue(row.TotalTags ?? row.totalTags ?? row.totaltags ?? ''),
    TotalNotes: asValue(row.TotalNotes ?? row.totalNotes ?? row.totalnotes ?? ''),
    HistoryCount: asValue(
      row.HistoryCount ?? row.historyCount ?? row.historycount ?? ''
    ),
  };
}

function mapHistoryRows(rows = []) {
  return rows.map(normalizeHistoryRow);
}

/**
 * Safe fetch that:
 *  - always reads as text first
 *  - checks content-type
 *  - parses JSON only if type is JSON
 *  - throws helpful errors with a preview of the body when non-JSON
 */
async function safeFetchJSON(url, init) {
  const res = await fetch(url, {
    // ensure Laravel returns JSON on exceptions too
    headers: { Accept: 'application/json', ...(init && init.headers) },
    ...init,
  });
  const text = await res.text();
  const ct = res.headers.get('content-type') || '';
  const trimmed = text.trim();
  const looksJson = trimmed.startsWith('{') || trimmed.startsWith('[');

  // Quick debug in console if needed
  // console.log('HISTORY RAW', res.status, ct, text.slice(0, 300));

  if (!ct.includes('application/json') && !looksJson) {
    const preview = text.slice(0, 200);
    throw new Error(`Non-JSON response (${res.status}). Body: ${preview}`);
  }

  let json;
  try {
    json = JSON.parse(text);
  } catch (e) {
    throw new Error(`Bad JSON: ${e.message}. Body: ${text.slice(0, 200)}`);
  }

  if (!res.ok) {
    throw new Error(json?.message || `Request failed (${res.status})`);
  }
  return json;
}

/** ---------- main component ---------- */
/**
 * Usage:
 *   <ViewHistoryTab queryId={detailForm?.QueryID} />
 */
export default function ViewHistoryTab({ queryId }) {
  const [data, setData]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // If you later add a manual input, keep this memo
  const effectiveQid = useMemo(
      () => (queryId || '').toString().trim(),
      [queryId]
  );

  const abortRef = useRef(null);

  const loadHistory = async () => {
    if (!effectiveQid) {
      setData([]);
      setErr('');
      return;
    }

    try {
      setLoading(true);
      setErr('');

      // Abort previous in-flight request
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      const url = `${API_BASE}/Search-history/${encodeURIComponent(effectiveQid)}/history`;
      const json = await safeFetchJSON(url, { signal: ac.signal });

      const rows = Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json?.Data)
          ? json.Data
          : Array.isArray(json?.history)
            ? json.history
            : Array.isArray(json)
              ? json
              : [];

      const fallbackRows = fallbackRowsByQueryId[effectiveQid] || [];
      const mapped = mapHistoryRows(rows.length ? rows : fallbackRows);

      setData(mapped);
    } catch (e) {
      // If aborted, ignore
      if (e.name === 'AbortError') return;
      setErr(e.message || 'Failed to load history');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveQid]);

  return (
      <div className="card p-3 mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">View History</h5>
          
        </div>

        {!effectiveQid && (
            <div className="alert alert-warning py-2">
              No Query ID provided.
            </div>
        )}

        {err && <div className="alert alert-danger py-2">{err}</div>}

        <DataTable
            columns={columns}
            data={data}
            pagination
            responsive
            highlightOnHover
            dense
            progressPending={loading}
        />
      </div>
  );
}
